import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

function formatElapsed(ms) {
  if (ms == null) return "--:--";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60).toString().padStart(2, "0");
  const s = (totalSec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function AdminPanel() {
  const [gameName, setGameName] = useState("");
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState("");

  // Checkpoints
  const [checkpoints, setCheckpoints] = useState([]);
  const [cpName, setCpName] = useState("");
  const [cpLat, setCpLat] = useState("");
  const [cpLng, setCpLng] = useState("");
  const [cpRadius, setCpRadius] = useState("50");

  // Teams
  const [teams, setTeams] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamUserId, setNewTeamUserId] = useState("");

  // ─── Fetch helpers ───────────────────────────────────────────────────────────

  const fetchGames = useCallback(async () => {
    try {
      const res = await axios.get("/api/games", { withCredentials: true });
      setGames(res.data);
    } catch (err) {
      console.error("Failed to fetch games:", err);
    }
  }, []);

  const fetchCheckpoints = useCallback(async (gameId) => {
    try {
      const res = await axios.get(`/api/games/${gameId}/checkpoints`, { withCredentials: true });
      setCheckpoints(res.data);
    } catch (err) {
      console.error("Failed to fetch checkpoints:", err);
    }
  }, []);

  const fetchTeams = useCallback(async (gameId) => {
    try {
      const res = await axios.get(`/api/games/${gameId}/teams`, { withCredentials: true });
      setTeams(res.data);
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    }
  }, []);

  const fetchUnassignedUsers = useCallback(async (gameId) => {
    try {
      const res = await axios.get(`/api/games/${gameId}/unassigned-users`, { withCredentials: true });
      setUnassignedUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  // ─── Polling ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchGames();
    const t = setInterval(fetchGames, 5000);
    return () => clearInterval(t);
  }, [fetchGames]);

  useEffect(() => {
    if (!selectedGameId) {
      setCheckpoints([]);
      setTeams([]);
      setUnassignedUsers([]);
      return;
    }

    fetchCheckpoints(selectedGameId);
    fetchTeams(selectedGameId);
    fetchUnassignedUsers(selectedGameId);

    // Poll team progress every 3s (for live elapsed times)
    const t = setInterval(() => {
      fetchTeams(selectedGameId);
    }, 3000);

    return () => clearInterval(t);
  }, [selectedGameId, fetchCheckpoints, fetchTeams, fetchUnassignedUsers]);

  // ─── Game actions ─────────────────────────────────────────────────────────────

  const createGame = async () => {
    if (!gameName.trim()) return alert("Enter a game name.");
    try {
      await axios.post("/api/games", { name: gameName }, { withCredentials: true });
      setGameName("");
      fetchGames();
    } catch {
      alert("Error creating game");
    }
  };

  const startGame = async () => {
    if (!selectedGameId) return;
    try {
      await axios.post(`/api/games/${selectedGameId}/start`, {}, { withCredentials: true });
      alert("Game started! Now use the Start buttons to send teams off.");
      fetchGames();
    } catch (err) {
      alert(err.response?.data?.error || "Error starting game");
    }
  };

  const endGame = async () => {
    if (!selectedGameId) return;
    if (!window.confirm("End the game and clear all teams/checkpoints?")) return;
    try {
      await axios.post(`/api/games/${selectedGameId}/end`, {}, { withCredentials: true });
      alert("Game ended.");
      setSelectedGameId("");
      fetchGames();
    } catch (err) {
      alert(err.response?.data?.error || "Error ending game");
    }
  };

  // ─── Checkpoint actions ───────────────────────────────────────────────────────

  const addCheckpoint = async () => {
    if (!cpName.trim() || !cpLat || !cpLng) return alert("Name, lat, and lng are required.");
    try {
      await axios.post(
        `/api/games/${selectedGameId}/checkpoints`,
        { name: cpName, lat: cpLat, lng: cpLng, radius: cpRadius || 50 },
        { withCredentials: true }
      );
      setCpName(""); setCpLat(""); setCpLng(""); setCpRadius("50");
      fetchCheckpoints(selectedGameId);
    } catch {
      alert("Error adding checkpoint");
    }
  };

  const removeCheckpoint = async (cpId) => {
    try {
      await axios.delete(`/api/games/${selectedGameId}/checkpoints/${cpId}`, { withCredentials: true });
      fetchCheckpoints(selectedGameId);
    } catch {
      alert("Error removing checkpoint");
    }
  };

  // ─── Team actions ─────────────────────────────────────────────────────────────

  const createTeam = async () => {
    if (!newTeamName.trim() || !newTeamUserId) return alert("Enter team name and select a user.");
    try {
      await axios.post(
        `/api/games/${selectedGameId}/teams`,
        { teamName: newTeamName, userId: newTeamUserId },
        { withCredentials: true }
      );
      setNewTeamName(""); setNewTeamUserId("");
      fetchTeams(selectedGameId);
      fetchUnassignedUsers(selectedGameId);
    } catch (err) {
      alert(err.response?.data?.error || "Error creating team");
    }
  };

  const removeTeam = async (teamId) => {
    try {
      await axios.delete(`/api/games/${selectedGameId}/teams/${teamId}`, { withCredentials: true });
      fetchTeams(selectedGameId);
      fetchUnassignedUsers(selectedGameId);
    } catch {
      alert("Error removing team");
    }
  };

  const startTeam = async (teamId, teamName) => {
    try {
      await axios.post(
        `/api/games/${selectedGameId}/teams/${teamId}/start`,
        {},
        { withCredentials: true }
      );
      fetchTeams(selectedGameId);
    } catch (err) {
      alert(err.response?.data?.error || `Error starting ${teamName}`);
    }
  };

  // ─── UI helpers ───────────────────────────────────────────────────────────────

  const selectedGame = games.find(g => g._id === selectedGameId);

  return (
    <div style={{ padding: "20px", maxWidth: "700px" }}>
      <h2 style={{ fontSize: "20px", fontWeight: "bold" }}>Admin Panel</h2>

      {/* ── Create Game ── */}
      <div style={{ marginTop: "16px" }}>
        <label style={{ fontWeight: "bold" }}>Create New Game</label>
        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
          <input
            type="text"
            placeholder="Game name"
            value={gameName}
            onChange={e => setGameName(e.target.value)}
            style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", flex: 1 }}
          />
          <button
            onClick={createGame}
            style={{ background: "#2563eb", color: "white", padding: "6px 14px", borderRadius: "4px", border: "none", cursor: "pointer" }}
          >
            Create
          </button>
        </div>
      </div>

      {/* ── Game List ── */}
      <div style={{ marginTop: "24px" }}>
        <h3 style={{ fontWeight: "600", marginBottom: "8px" }}>Game Sessions</h3>
        {games.length === 0 ? (
          <p style={{ color: "#6b7280" }}>No games yet.</p>
        ) : (
          games.map(game => (
            <div
              key={game._id}
              style={{
                border: selectedGameId === game._id ? "3px solid #2563eb" : "2px solid #e5e7eb",
                background: selectedGameId === game._id ? "#eff6ff" : "#fff",
                borderRadius: "8px", padding: "10px 14px", marginBottom: "8px",
                display: "flex", justifyContent: "space-between", alignItems: "center"
              }}
            >
              <div>
                <div style={{ fontWeight: "bold" }}>{game.name}</div>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>
                  {game.started ? "🟢 Live" : "🕒 Waiting"}
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedGameId(game._id);
                  window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                }}
                style={{ background: "#2563eb", color: "white", padding: "6px 12px", borderRadius: "4px", border: "none", cursor: "pointer" }}
              >
                Manage
              </button>
            </div>
          ))
        )}
      </div>

      {/* ── Selected Game Management ── */}
      {selectedGameId && (
        <>
          <h3 style={{ fontWeight: "bold", marginTop: "24px", fontSize: "16px" }}>
            Managing: {selectedGame?.name}
          </h3>

          {/* Game controls */}
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button
              onClick={startGame}
              disabled={!!selectedGame?.started}
              style={{
                background: selectedGame?.started ? "#6b7280" : "#16a34a",
                color: "white", padding: "8px 16px", borderRadius: "6px",
                border: "none", cursor: selectedGame?.started ? "default" : "pointer"
              }}
            >
              {selectedGame?.started ? "Game Live" : "Start Game"}
            </button>
            <button
              onClick={endGame}
              style={{ background: "#dc2626", color: "white", padding: "8px 16px", borderRadius: "6px", border: "none", cursor: "pointer" }}
            >
              End Game
            </button>
          </div>

          {/* ── Checkpoints ── */}
          <div style={{ marginTop: "24px" }}>
            <h4 style={{ fontWeight: "bold", marginBottom: "10px" }}>
              Checkpoints ({checkpoints.length})
            </h4>

            {/* Add checkpoint form */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "10px" }}>
              <input
                type="text"
                placeholder="Name (e.g. Capitol)"
                value={cpName}
                onChange={e => setCpName(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", gridColumn: "1/-1" }}
              />
              <input
                type="number"
                placeholder="Latitude"
                value={cpLat}
                onChange={e => setCpLat(e.target.value)}
                step="0.000001"
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px" }}
              />
              <input
                type="number"
                placeholder="Longitude"
                value={cpLng}
                onChange={e => setCpLng(e.target.value)}
                step="0.000001"
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px" }}
              />
              <input
                type="number"
                placeholder="Radius (m, default 50)"
                value={cpRadius}
                onChange={e => setCpRadius(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px" }}
              />
              <button
                onClick={addCheckpoint}
                style={{ background: "#10b981", color: "white", padding: "8px", borderRadius: "6px", border: "none", cursor: "pointer" }}
              >
                Add Checkpoint
              </button>
            </div>

            {checkpoints.length === 0 ? (
              <p style={{ color: "#6b7280", fontSize: "14px" }}>No checkpoints yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Name</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Lat</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Lng</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Radius</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {checkpoints.map(cp => (
                    <tr key={cp._id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "6px 8px" }}>{cp.name}</td>
                      <td style={{ padding: "6px 8px" }}>{cp.lat.toFixed(5)}</td>
                      <td style={{ padding: "6px 8px" }}>{cp.lng.toFixed(5)}</td>
                      <td style={{ padding: "6px 8px" }}>{cp.radius}m</td>
                      <td style={{ padding: "6px 8px" }}>
                        <button
                          onClick={() => removeCheckpoint(cp._id)}
                          style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", fontSize: "12px" }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Teams ── */}
          <div style={{ marginTop: "28px" }}>
            <h4 style={{ fontWeight: "bold", marginBottom: "10px" }}>
              Teams ({teams.length})
            </h4>

            {/* Create team */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="Team name"
                value={newTeamName}
                onChange={e => setNewTeamName(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", flex: "1 1 140px" }}
              />
              <select
                value={newTeamUserId}
                onChange={e => setNewTeamUserId(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", flex: "1 1 140px" }}
              >
                <option value="">Select player…</option>
                {unassignedUsers.map(u => (
                  <option key={u._id} value={u._id}>{u.username}</option>
                ))}
              </select>
              <button
                onClick={createTeam}
                style={{ background: "#7c3aed", color: "white", padding: "8px 14px", borderRadius: "6px", border: "none", cursor: "pointer" }}
              >
                Add Team
              </button>
            </div>

            {/* Team progress table */}
            {teams.length === 0 ? (
              <p style={{ color: "#6b7280", fontSize: "14px" }}>No teams yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Team</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Player</th>
                    <th style={{ padding: "6px 8px", textAlign: "center" }}>Progress</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Time</th>
                    <th style={{ padding: "6px 8px", textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map(t => (
                    <tr key={t._id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "6px 8px", fontWeight: "bold" }}>{t.teamName}</td>
                      <td style={{ padding: "6px 8px", color: "#6b7280" }}>{t.username}</td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        <span style={{
                          background: t.finished ? "#d1fae5" : "#fef9c3",
                          padding: "2px 8px", borderRadius: "12px", fontSize: "12px"
                        }}>
                          {t.finished ? "Done" : `${t.checkpointsCollected}/${t.totalCheckpoints}`}
                        </span>
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "monospace" }}>
                        {t.finished
                          ? formatElapsed(t.elapsedMs)
                          : t.raceStarted
                            ? formatElapsed(t.elapsedMs)
                            : "—"}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                          {!t.raceStarted ? (
                            <button
                              onClick={() => startTeam(t._id, t.teamName)}
                              disabled={!selectedGame?.started}
                              style={{
                                background: selectedGame?.started ? "#16a34a" : "#6b7280",
                                color: "white", border: "none", borderRadius: "4px",
                                padding: "4px 10px", cursor: selectedGame?.started ? "pointer" : "default",
                                fontSize: "12px"
                              }}
                            >
                              Start
                            </button>
                          ) : (
                            <span style={{ color: "#16a34a", fontSize: "12px" }}>Started</span>
                          )}
                          <button
                            onClick={() => removeTeam(t._id)}
                            style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", fontSize: "12px" }}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
